import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { 
  Mail, 
  ArrowLeft,
  CheckCircle,
  Loader2
} from 'lucide-react'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
      } else {
        setError(data.error || 'Failed to send reset email')
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/IAS-C-logo-blue.png" 
              alt="Impact Accelerator Circle" 
              className="w-32 h-32 mx-auto mb-6 logo-container"
              style={{ width: '128px', height: 'auto' }}
            />
          </div>

          {/* Success Message */}
          <Card className="rounded-3xl shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-gray-900">Check Your Email</CardTitle>
              <CardDescription className="text-gray-600">
                We've sent password reset instructions to your email address
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <Alert className="rounded-2xl border-green-200 bg-green-50">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  If that email exists in our system, we've sent password reset instructions. 
                  Please check your email and follow the link to reset your password.
                </AlertDescription>
              </Alert>

              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      setSuccess(false)
                      setEmail('')
                    }}
                    variant="outline"
                    className="w-full rounded-2xl"
                  >
                    Try Different Email
                  </Button>
                  
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="w-full rounded-2xl text-blue-600 hover:text-blue-700"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Links */}
          <div className="text-center mt-8 space-y-2">
            {/* TODO: Re-enable when bug report backend is implemented
            <Link to="/report-bug" className="block text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Report a Bug
            </Link>
            */}
            <p className="text-xs text-gray-400">
              © 2025 Impact Accelerator Circle. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/IAS-C-logo-blue.png" 
            alt="Impact Accelerator Circle" 
            className="w-32 h-32 mx-auto mb-6 logo-container"
            style={{ width: '128px', height: 'auto' }}
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">Enter your email to receive reset instructions</p>
        </div>

        {/* Forgot Password Form */}
        <Card className="rounded-3xl shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-gray-900">Reset Password</CardTitle>
            <CardDescription className="text-gray-600">
              We'll send you a secure link to reset your password
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="rounded-2xl border-red-200 bg-red-50">
                  <div className="w-4 h-4 bg-red-600 rounded-full" />
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-2xl h-12 pl-10"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send Reset Instructions'
                )}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to Login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="text-center mt-8 space-y-2">
          {/* TODO: Re-enable when bug report backend is implemented
          <Link to="/report-bug" className="block text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Report a Bug
          </Link>
          */}
          <p className="text-xs text-gray-400">
            © 2025 Impact Accelerator Circle. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
